import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useTranslation } from 'react-i18next';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// 🔹 영어 → 한국어 매핑
const nameMap = {
  Traffic_light: "신호등",
  Traffic_sign: "교통 표지판",
  Manhole: "맨홀",
  Drain: "배수구",
  Streetlight: "가로등",
  solid_line: "실선",
  dotted_line: "점선",
  lane_line: "차선",
  stop_line: "정지선",
  arrow: "화살표",
  crosswalk: "횡단보도",
  diamond: "횡단보도 예고",
  zigzag: "지그재그",
  center_line: "중앙선",
  triangle: "삼각 표시",
  safety_zone: "안전지대",
  lettering: "문자 표시",
  edge_line: "길 가장자리 구역선",
  bus_stop: "버스 정류장",
  speed_bump: "과속방지턱",
  tire: "타이어",
  rock: "돌",
  traffic_cone: "안전콘",
  styrofoam_box: "스티로폼 박스",
  cardboard_box: "골판지 상자",
  patch: "포트홀 (보수완료)",
  pothole: "포트홀",
  linear_crack: "크랙",
};

const DetectionDashboard = ({ detectionData }) => {
  const { t } = useTranslation('utils');
  const [classKeys, setClassKeys] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (detectionData?.class_statistics) {
      const keys = Object.keys(detectionData.class_statistics);
      setClassKeys(keys);
      setActiveTab(keys[0] || "");
    }
  }, [detectionData]);

  if (!detectionData?.class_statistics || !classKeys.length || !activeTab)
    return null;

  const stats = detectionData.class_statistics[activeTab];
  if (!stats) return null;

  // 🔹 그래프 색상 (각 값 개별 색)
  const graphColors = ["#FF6384", "#36A2EB", "#FFCE56"];

  const graphData = {
    labels: [`${t("dashbord.lowest")}`, `${t("dashbord.average")}`, `${t("dashbord.maximum")}`],
    datasets: [
      {
        label: nameMap[activeTab] || activeTab,
        data: [stats.min_confidence, stats.avg_confidence, stats.max_confidence],
        backgroundColor: graphColors,
      },
    ],
  };

  const graphOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, max: 1 } },
  };

  // 🔥 열림 / 닫힘 width & height 조절
  const containerStyle = {
    background: "#2B2E3A",
    borderRadius: 8,
    padding: "10px 12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
    overflow: "hidden",
    transition: "width 0.3s ease, height 0.3s ease",
    width: isOpen ? "100%" : "200px", // 🔥 접으면 폭 축소
    height: isOpen ? "auto" : "45px", // 🔥 헤더만 남김
    cursor: "pointer",
    boxSizing: "border-box",
  };

  return (
    <div style={containerStyle} onClick={() => setIsOpen(!isOpen)}>
      {/* 헤더 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#FFFFFF",
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        {t("dashbord.title")}
        <span style={{ fontSize: 20 }}>{isOpen ? "─" : "+"}</span>
      </div>

      {/* 🔥 닫혀있으면 아래 내용 숨김 */}
      {!isOpen ? null : (
        <div style={{ marginTop: 12 }}>
          {/* 탭 */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "5px",
              marginBottom: "5px",
              maxHeight: "80px",
              overflowY: "auto",
              justifyContent: "center",
            }}
          >
            {classKeys.map((key) => (
              <button
                key={key}
                onClick={(e) => {
                  e.stopPropagation(); // 버튼 누르면 접히는 이벤트 차단
                  setActiveTab(key);
                }}
                style={{
                  padding: "4px 8px",
                  backgroundColor: key === activeTab ? "#9D91FF" : "#3A3E4E",
                  color: "#FFFFFF",
                  borderRadius: 20,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {nameMap[key] || key}
              </button>
            ))}
          </div>

          {/* 그래프 */}
          <div style={{ width: "100%", height: "180px", marginBottom: "10px" }}>
            <Bar data={graphData} options={graphOptions} />
          </div>

          {/* Count */}
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              textAlign: "center",
              color: "#FFFFFF",
            }}
          >
            {t("dashbord.total")}: <span style={{ color: "#9D91FF" }}>{stats.count}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetectionDashboard;
